import React from 'react';
import Markdown from 'markdown-to-jsx';
import Button from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import Image from '@atlaskit/image';
import { Box, Stack } from '@atlaskit/primitives';
import {
	srcDark as TaskFiveIllustrationDark,
	srcLight as TaskFiveIllustrationLight,
} from '@atlassian/jira-illustrations/src/ui/adg4/brand/spots/devices/components/android-black-chat';
import {
	srcDark as TaskOneIllustrationDark,
	srcLight as TaskOneIllustrationLight,
} from '@atlassian/jira-illustrations/src/ui/adg4/brand/spots/other/components/integrations';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import { useIsAdmin, useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller';
import { Anchor } from '../../../common/ui/anchor';
import { TransitionHomeContentBox } from '../../../common/ui/transition-home/content-box';
import { TransitionHomeHeader } from '../../../common/ui/transition-home/header';
import { messages as stepMessages } from '../../../common/ui/transition-home/step/messages';
import taskMessages from '../../../common/utils/tasks/messages';
import {
	useDeprecatedIntegrations,
	useDiscontinuedIntegrations,
	useIncomingCallIntegrations,
	useIntegrationCounts,
	useSyncedIntegrations,
} from '../../../services/store/integrations';
import TaskTwoIllustration from './assets/integrations-task-two-illustration.svg';
import { DeprecatedIntegrationsTable } from './deprecated-integrations-table';
import { DiscontinuedIntegrationsTable } from './discontinued-integrations-table';
import { messages } from './messages';
import { SyncedIntegrationsTable } from './synced-integrations-table';

export const IntegrationsStep = () => {
	const { formatMessage } = useIntl();

	const [{ counts }] = useIntegrationCounts();
	const [{ syncedIntegrations, hasSyncedIntegration }] = useSyncedIntegrations();
	const [{ deprecatedIntegrations, hasDeprecatedIntegration }] = useDeprecatedIntegrations();
	const [{ discontinuedIntegrations, hasDiscontinuedIntegration }] = useDiscontinuedIntegrations();
	const [{ incomingCallIntegrations, hasIncomingCallIntegration }] = useIncomingCallIntegrations();
	const isSiteAdmin = useIsSiteAdmin();
	const isPrimaryAdmin = useIsAdmin();

	if (
		!counts.data ||
		!deprecatedIntegrations.data ||
		!incomingCallIntegrations.data ||
		!syncedIntegrations.data ||
		!discontinuedIntegrations.data
	) {
		throw new Error('IntegrationsStep - integrations data should be loaded at this point');
	}

	const totalCount = counts.data.totalCount;
	const notSyncedIntegrationCount = counts.data.chatCount + deprecatedIntegrations.data?.count;
	const syncedIntegrationCount = counts.data.totalCount - notSyncedIntegrationCount;

	return (
		<Stack space="space.250">
			<TransitionHomeHeader
				title={<FormattedMessage {...stepMessages.integrationsTitleNonFinal} />}
				description={<FormattedMessage {...stepMessages.integrationsDescriptionNonFinal} />}
			/>

			{/* Task One */}
			<TransitionHomeContentBox
				illustration={
					<Image
						src={TaskOneIllustrationLight}
						srcDark={TaskOneIllustrationDark}
						alt=""
						width={130}
					/>
				}
			>
				<Stack space="space.200">
					<Heading as="h2" size="small">
						<FormattedMessage {...taskMessages.integrationsTaskOneNonFinal} />
					</Heading>
					<Box>
						<Markdown>
							{formatMessage(messages.taskOneDescriptionNonFinal, {
								totalCount,
								syncedIntegrationCount,
								notSyncedIntegrationCount,
							})}
						</Markdown>
					</Box>
					<Box>
						<Button href="/jira/settings/products/ops/integrations" target="_blank">
							<FormattedMessage {...messages.taskOneActionButtonNonFinal} />
						</Button>
					</Box>
				</Stack>
			</TransitionHomeContentBox>

			{/* Task Two */}
			{hasSyncedIntegration && (
				<TransitionHomeContentBox illustration={<Image src={TaskTwoIllustration} alt="" />}>
					<Stack space="space.200">
						<Heading as="h2" size="small">
							<FormattedMessage {...taskMessages.integrationsTaskTwoNonFinal} />
						</Heading>
						<Box>
							<Markdown
								options={{
									overrides: {
										a: {
											component: Anchor,
										},
									},
								}}
							>
								{formatMessage(messages.taskTwoDescriptionNonFinal)}
							</Markdown>
						</Box>
						<SyncedIntegrationsTable syncedIntegrations={syncedIntegrations} />
					</Stack>
				</TransitionHomeContentBox>
			)}

			{/* Task Three */}
			{hasDiscontinuedIntegration && (
				<TransitionHomeContentBox>
					<Stack space="space.200">
						<Heading as="h2" size="small">
							<FormattedMessage {...taskMessages.integrationsTaskThreeNonFinal} />
						</Heading>
						<Box>
							<Markdown>{formatMessage(messages.taskThreeDescriptionNonFinal)}</Markdown>
						</Box>
						<DiscontinuedIntegrationsTable discontinuedIntegrations={discontinuedIntegrations} />
					</Stack>
				</TransitionHomeContentBox>
			)}

			{/* Task Four */}
			{hasDeprecatedIntegration && (
				<TransitionHomeContentBox>
					<Stack space="space.200">
						<Heading as="h2" size="small">
							<FormattedMessage {...taskMessages.integrationsTaskFourNonFinal} />
						</Heading>
						<Box>
							<Markdown>{formatMessage(messages.taskFourDescriptionNonFinal)}</Markdown>
						</Box>
						<DeprecatedIntegrationsTable deprecatedIntegrations={deprecatedIntegrations} />
					</Stack>
				</TransitionHomeContentBox>
			)}

			{/* Task Five */}
			{/* Display only if user is site admin or product admin */}
			{hasIncomingCallIntegration && (isSiteAdmin || isPrimaryAdmin) && (
				<TransitionHomeContentBox
					illustration={
						<Image
							src={TaskFiveIllustrationLight}
							srcDark={TaskFiveIllustrationDark}
							alt=""
							width={139}
						/>
					}
				>
					<Stack space="space.200">
						<Heading as="h2" size="small">
							<FormattedMessage {...taskMessages.integrationsTaskFiveNonFinal} />
						</Heading>
						<Box>
							<Markdown>
								{formatMessage(messages.taskFiveDescriptionNonFinal, {
									incomingCallIntegrations: incomingCallIntegrations.data.count,
								})}
							</Markdown>
						</Box>
						<Box>
							<Button href="/jira/settings/products/ops/incoming-call" target="_blank">
								<FormattedMessage {...messages.taskFiveActionButtonNonFinal} />
							</Button>
						</Box>
					</Stack>
				</TransitionHomeContentBox>
			)}
		</Stack>
	);
};
